// The spike's audio content as @rpg/audio's options (gate D2.4): the cue tables (the kits' `sfx`, @rpg-unit/sfx-kits,
// then the game's own `sounds`: the cutscene sting, the beds and spots), the mix, pool, beds, spots and moods by place
// (audio-mix.json), the music (music.json picks the presets; @rpg-unit/adaptive-music resolves the mood, band and
// instrument presets into the director's table and instruments) and the babble voices (voices.json). Values only; the
// mechanism is the package's. Since the audio units (2026-10-20) the music and the kit cues are referenced, not copied.
import type { AudioOptions, BabbleVoice, BedDef, IrSpec, SoundDef, SpotDef } from "@rpg/audio";
import { moodFor as moodByPlace, resolveScore, type ScorePick, type ScoreRows } from "@rpg-unit/adaptive-music";
import { pickCues, SFX_KITS, toSoundDef, type SfxKit, type SfxPreset } from "@rpg-unit/sfx-kits";
import type { SpikeContent } from "../content";
import type { AudioMixRow, MusicRow, SoundRow, VoiceRow } from "../content/types.gen";

/** The mix row and the music row (each table has one, `default`). */
export function audioRows(content: SpikeContent): { mix: AudioMixRow; music: MusicRow } {
  const mix = content.registry.get("audio-mix", "default") as AudioMixRow | undefined;
  const music = content.registry.get("music", "default") as MusicRow | undefined;
  if (!mix || !music) throw new Error("audio: the audio-mix or music table has no 'default' row");
  return { mix, music };
}

/** The presets the score draws on, as the content loaded them (the units' tables in the registry). */
export function scoreRows(content: SpikeContent): ScoreRows {
  const rows = <T>(t: "music-moods" | "music-combat" | "music-stings" | "instruments" | "ensembles"): T => content.registry.rows(t) as unknown as T;
  return { moods: rows("music-moods"), combat: rows("music-combat"), stings: rows("music-stings"), instruments: rows("instruments"), ensembles: rows("ensembles") };
}

/** The score music.json picks: its moods, its fight layer and its victory sting (preset ids). */
export function scorePick(music: MusicRow): ScorePick {
  return { moods: music.moods.map((m) => m.id), combat: music.combat.id, victory: music.victory.id };
}

/** The kit cues the page loads (improvement step 3, IMP-0022): every kit whole but the footsteps, and of those only
 *  the steps the mix's surfaces name, so the warm-up never renders a step nobody plays (the kit's stone, since round 2
 *  plays the game's own). In table order: every cue kept renders as before. */
export function kitRows(content: SpikeContent): SfxPreset[] {
  const kit = content.registry.rows("sfx") as unknown as readonly SfxPreset[];
  const named = new Set(Object.values(audioRows(content).mix.footsteps));
  return pickCues(kit, { kits: (Object.keys(SFX_KITS) as SfxKit[]).filter((k) => k !== "footsteps"), cues: kit.filter((r) => r.kit === "footsteps" && named.has(r.id)).map((r) => r.id) });
}

/** Every cue: the kits' the page loads (they were this table's first rows, so the warm-up's order is unchanged), then
 *  the game's. */
export function cueRows(content: SpikeContent): readonly SfxPreset[] {
  return [...kitRows(content), ...(content.registry.rows("sounds") as readonly SoundRow[] as unknown as readonly SfxPreset[])];
}

/** The engine's options from the tables, for a tier ("low": the phone's pool and panning). `strip` names an important
 *  cue to leave without its caption (the planted defect). */
export function audioOptions(content: SpikeContent, tier: string, o: { strip?: string | null; seed?: number } = {}): AudioOptions {
  const { mix, music } = audioRows(content);
  const sounds: Record<string, SoundDef> = {};
  for (const r of cueRows(content)) sounds[r.id] = toSoundDef(r, o.strip === r.id ? { caption: null } : {});
  const voices: Record<string, BabbleVoice> = {};
  for (const v of content.registry.rows("voices") as readonly VoiceRow[]) voices[v.id] = { pitch: v.pitch, formant: v.formant, rate: v.rate, breath: v.breath, rough: v.rough };
  // the score: the presets music.json picks, resolved; each instrument a multisample: its desktop roots, or fewer on a
  // phone (less to render at load)
  const score = resolveScore(scorePick(music), scoreRows(content), { light: tier === "low" });
  const pool = tier === "low" ? mix.pool.low : mix.pool.high;
  return {
    sounds,
    duck: mix.duck,
    master: mix.master,
    pool: { voices: pool.voices, loops: pool.loops, hrtfWithin: pool.hrtfWithin, refDistance: mix.pool.refDistance, rolloff: mix.pool.rolloff, air: mix.pool.air, muffle: mix.pool.muffle },
    music: {
      table: score.table,
      instruments: score.instruments,
      mood: mix.moods.default,
      lookahead: mix.lookahead,
      // the music's space: the phone's room is shorter (a cheaper convolution)
      space: { ir: (tier === "low" ? mix.space.low : mix.space.high) as IrSpec, reverb: mix.space.reverb, ...(mix.space.delay ? { delay: mix.space.delay } : {}) },
    },
    ambience: { beds: mix.beds as BedDef[], spots: mix.spots as SpotDef[], fade: mix.fade },
    voices,
    babbleGain: mix.babbleGain,
    seed: o.seed ?? 11,
  };
}

/** The mood for a place and time: a time's mood wins over the area's, else the default (@rpg-unit/adaptive-music's
 *  rule, over the mix's moods by place). */
export function moodFor(mix: AudioMixRow, area: string | null, time: string | null): string {
  return moodByPlace(mix.moods, { area, time });
}
